// a class to handler communication between an iframe and the parent window

import {
  CommunicationDataTransfer,
  CommunicationDataType,
  GameDataDefinition,
  isCommunicationDataTransfer,
} from '@toilet-paper-games/core';
import { BaseCommunicator } from '@toilet-paper-games/core';

import { hosterAppModel } from '@/hoster/App';

import { NetworkDataType } from '../../common/models/NetworkDataTransfers';
import { config } from '../../common/config';
import { HosterGamePlayerViewModel } from '../components/HosterGamePlayer';
import { ignoredCommunicationDataDebugMessageTypes } from '@/common/constants';

export class CommunicationHandlerAppHoster<
  T extends GameDataDefinition,
> extends BaseCommunicator<T> {
  messenger: (data: unknown) => void = () => {
    throw new Error('Messenger not set');
  };

  /** setupMessenger must be used to setup messenger */
  constructor(private hosterGamePlayerViewModel: HosterGamePlayerViewModel) {
    super();
  }

  setupMessenger(messenger: (data: unknown) => void) {
    this.messenger = messenger;
  }

  sendToFrame(data: CommunicationDataTransfer<T>) {
    if (!isCommunicationDataTransfer(data)) {
      throw new Error('Invalid communication data transfer');
    }

    if (config.debugMode) {
      if (!ignoredCommunicationDataDebugMessageTypes.includes(data.type)) {
        console.info('[TPG] Sending to frame', data);
      }
    }

    this.messenger(data);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  messageHandler(data: unknown) {
    super.messageHandler(data);

    if (!isCommunicationDataTransfer(data)) {
      throw new Error('Invalid communication data transfer');
    }

    if (config.debugMode) {
      if (!ignoredCommunicationDataDebugMessageTypes.includes(data.type)) {
        console.info('[TPG] Receiving from frame', data);
      }
    }

    if (data.type === CommunicationDataType.DEBUG) {
      hosterAppModel.networkHandler.broadcast({
        type: NetworkDataType.DEBUG,
        data: data.data,
      });
    }

    if (data.type === CommunicationDataType.PING_HOSTER) {
      const user = hosterAppModel.networkHandler.userManager.usersMap.get(
        data.data.playerId,
      );

      if (!user) {
        throw new Error('User not found');
      }

      const connection = user.connection;

      if (!connection) {
        throw new Error('Connection not found');
      }

      connection.send({
        type: NetworkDataType.PING_H2C,
        data: {
          hosterTime: data.data.hosterTime,
          id: data.data.id,
          userId: data.data.playerId,
          timeSinceStart: data.data.timeSinceStart,
        },
      });
    }

    if (data.type === CommunicationDataType.PONG_HOSTER) {
      const user = hosterAppModel.networkHandler.userManager.usersMap.get(
        data.data.playerId,
      );

      if (!user) {
        throw new Error('User not found');
      }

      const connection = user.connection;

      if (!connection) {
        throw new Error('Connection not found');
      }

      connection.send({
        type: NetworkDataType.PONG_H2C,
        data: {
          hosterTime: data.data.hosterTime,
          id: data.data.id,
          pingMs: data.data.pingMs,
          userId: data.data.playerId,
          timeSinceStart: data.data.timeSinceStart,
        },
      });
    }

    if (data.type === CommunicationDataType.GAME_ACTION_HOSTER) {
      const user = hosterAppModel.networkHandler.userManager.usersMap.get(data.data.to);

      if (!user) {
        throw new Error('User not found');
      }

      const connection = user.connection;

      if (!connection) {
        throw new Error('Connection not found');
      }

      connection.send({
        type: NetworkDataType.GAME_ACTION,
        data: data.data.payload,
      });
    }

    if (data.type === CommunicationDataType.END_GAME_HOSTER) {
      hosterAppModel.endGame();
    }

    if (data.type === CommunicationDataType.RELOAD_GAME_HOSTER) {
      hosterAppModel.networkHandler.broadcast({
        type: NetworkDataType.REFRESH,
        data: null,
      });
      this.hosterGamePlayerViewModel.props.iFrameLoaderRef.current?.reload();
    }
  }

  addReadyListener(listener: () => void): { destroy: () => void } {
    const readyListener = (message: CommunicationDataTransfer<T>) => {
      if (
        message.type === CommunicationDataType.READY_STATUS_HOSTER &&
        message.data.ready
      ) {
        listener();
      }
    };

    const destructor = this.addAppMessageListener(readyListener);

    return destructor;
  }

  waitForReady(): Promise<void> {
    return new Promise((res) => {
      const destructor = this.addReadyListener(() => {
        res();
        destructor.destroy();
      });
    });
  }
}
